import React, { useEffect, useState } from "react";
import axios from "axios";

export default function NotificationsPage() {
  const [cfg, setCfg]         = useState(null);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [testing, setTesting] = useState(null); // "email" | "telegram"
  const [showTgHelp, setShowTgHelp] = useState(false);
  const [serverCfg, setServerCfg] = useState(null); // {telegram_token_set, smtp_configured}

  useEffect(() => {
    fetchSettings();
    axios.get("/settings/notifications/config-status")
      .then(r => setServerCfg(r.data))
      .catch(() => {});
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get("/settings/notifications");
      setCfg(res.data);
    } catch {
      setCfg({
        email_enabled: false, email_address: "",
        telegram_enabled: false, telegram_chat_id: "",
        notify_on_suggestion: true, notify_on_order_fill: true,
        notify_on_order_fail: true, notify_on_error: true,
      });
    }
  };

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put("/settings/notifications", cfg);
      flash("Notification settings saved.");
    } catch {
      flash("Failed to save settings.", false);
    } finally {
      setSaving(false);
    }
  };

  const testChannel = async (channel) => {
    const to = channel === "email" ? cfg.email_address : cfg.telegram_chat_id;
    if (!to) {
      flash(channel === "email"
        ? "Enter your email address first."
        : "Enter your Telegram Chat ID first.", false);
      return;
    }
    setTesting(channel);
    try {
      const res = await axios.post("/settings/notifications/test", { channel, to });
      flash(res.data.ok
        ? `Test ${channel === "telegram" ? "Telegram message" : "email"} sent!`
        : (res.data.error || "Send failed."),
        res.data.ok);
    } catch (e) {
      flash(e?.response?.data?.error || "Test failed.", false);
    } finally {
      setTesting(null);
    }
  };

  const set = (key, val) => setCfg(c => ({ ...c, [key]: val }));

  if (!cfg) return <div style={{ padding: 25, color: "#888" }}>Loading…</div>;

  return (
    <div style={{ padding: 25, background: "#f4f6f9", minHeight: "100vh" }}>

      <h2 style={pageTitle}>Notification Settings</h2>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 24, marginTop: -8 }}>
        Configure how you receive alerts for trade suggestions, order fills, and errors.
      </p>

      {msg && (
        <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>
      )}

      {/* ── Server config warnings ── */}
      {serverCfg && (!serverCfg.telegram_token_set || !serverCfg.smtp_configured) && (
        <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#856404", marginBottom: 8 }}>
            Server configuration required
          </div>
          <div style={{ fontSize: 13, color: "#6d5a00", lineHeight: 1.7 }}>
            The following credentials are not set in <code style={code}>backend/.env</code>:
            <ul style={{ margin: "6px 0 0 0", paddingLeft: 20 }}>
              {!serverCfg.smtp_configured && (
                <li>
                  <strong>Email (SMTP)</strong> — add <code style={code}>SMTP_HOST</code>, <code style={code}>SMTP_PORT</code>,{" "}
                  <code style={code}>SMTP_USER</code>, <code style={code}>SMTP_PASS</code> to <code style={code}>backend/.env</code>
                </li>
              )}
              {!serverCfg.telegram_token_set && (
                <li>
                  <strong>Telegram</strong> — add <code style={code}>TELEGRAM_BOT_TOKEN=&lt;token&gt;</code> to <code style={code}>backend/.env</code>,
                  then restart Docker (<code style={code}>docker compose up -d</code>)
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* ── Email ── */}
      <div style={card}>
        <div style={cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Toggle checked={cfg.email_enabled} onChange={v => set("email_enabled", v)} />
            <h5 style={cardTitle}>Email Notifications</h5>
          </div>
        </div>
        <div style={{ ...cardBody, opacity: cfg.email_enabled ? 1 : 0.5, pointerEvents: cfg.email_enabled ? "auto" : "none" }}>
          <div style={fieldRow}>
            <div style={fieldBox}>
              <div style={fieldLabel}>Email Address</div>
              <input
                type="email"
                value={cfg.email_address || ""}
                onChange={e => set("email_address", e.target.value)}
                placeholder="you@example.com"
                style={input} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={() => testChannel("email")}
                disabled={testing === "email"}
                style={btnTest}>
                {testing === "email" ? "Sending…" : "Send Test Email"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Telegram ── */}
      <div style={card}>
        <div style={cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Toggle checked={cfg.telegram_enabled} onChange={v => set("telegram_enabled", v)} />
            <h5 style={cardTitle}>Telegram Notifications</h5>
            <span style={tgBadge}>via Bot API</span>
          </div>
        </div>
        <div style={{ ...cardBody, opacity: cfg.telegram_enabled ? 1 : 0.5, pointerEvents: cfg.telegram_enabled ? "auto" : "none" }}>
          <div style={fieldRow}>
            <div style={fieldBox}>
              <div style={fieldLabel}>Your Telegram Chat ID</div>
              <input
                type="text"
                value={cfg.telegram_chat_id || ""}
                onChange={e => set("telegram_chat_id", e.target.value)}
                placeholder="e.g. 123456789"
                style={{ ...input, fontFamily: "monospace" }} />
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                Not sure of your Chat ID?{" "}
                <span
                  style={{ color: "#0088cc", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => setShowTgHelp(h => !h)}>
                  {showTgHelp ? "Hide instructions" : "How to get it"}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={() => testChannel("telegram")}
                disabled={testing === "telegram"}
                style={{ ...btnTest, background: "#0088cc", borderColor: "#0088cc" }}>
                {testing === "telegram" ? "Sending…" : "Send Test Message"}
              </button>
            </div>
          </div>

          {/* Telegram setup instructions */}
          {showTgHelp && (
            <div style={helpBox}>
              <div style={helpTitle}>How to set up Telegram notifications</div>
              <ol style={helpList}>
                <li>
                  <strong>Start a chat with the Growfin bot</strong><br />
                  Open Telegram and search for your bot username (ask your admin for the bot name),
                  then press <em>Start</em>.
                </li>
                <li>
                  <strong>Find your Chat ID</strong><br />
                  Open Telegram and message{" "}
                  <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" style={link}>
                    @userinfobot
                  </a>
                  . It will instantly reply with your numeric Chat ID (e.g. <code style={code}>123456789</code>).
                </li>
                <li>
                  <strong>Paste the Chat ID above</strong> and click <em>Send Test Message</em> to verify.
                </li>
              </ol>
              <div style={helpNote}>
                <strong>Admin note:</strong> The Telegram bot token must be set as{" "}
                <code style={code}>TELEGRAM_BOT_TOKEN</code> in the backend environment
                (docker-compose.yml). Create a bot via{" "}
                <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" style={link}>
                  @BotFather
                </a>{" "}
                — send <code style={code}>/newbot</code>, follow the prompts, and copy the token.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Event triggers ── */}
      <div style={card}>
        <div style={cardHeader}>
          <h5 style={cardTitle}>Notify Me When…</h5>
        </div>
        <div style={cardBody}>
          <div style={eventGrid}>
            {[
              { key: "notify_on_suggestion",  label: "New Trade Suggestion", desc: "When the rule engine generates a new action" },
              { key: "notify_on_order_fill",  label: "Order Filled",         desc: "When a broker order is successfully executed" },
              { key: "notify_on_order_fail",  label: "Order Failed",         desc: "When a broker order fails or is rejected" },
              { key: "notify_on_error",       label: "System Error",         desc: "When a critical system error occurs" },
            ].map(({ key, label, desc }) => (
              <div key={key} style={eventCard}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#2c3e50" }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{desc}</div>
                  </div>
                  <Toggle checked={cfg[key]} onChange={v => set(key, v)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Save */}
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={save} disabled={saving} style={btnPrimary}>
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

/* ── Toggle ───────────────────────────────────────────────────── */

function Toggle({ checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11, cursor: "pointer",
        background: checked ? "#007bff" : "#ccc",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}>
      <div style={{
        position: "absolute", top: 3, left: checked ? 21 : 3,
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
      }} />
    </div>
  );
}

/* ── Styles ───────────────────────────────────────────────────── */

const pageTitle  = { fontSize: 22, fontWeight: 700, color: "#2c3e50", marginBottom: 4 };
const toast      = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const card       = { background: "#fff", borderRadius: 12, marginBottom: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" };
const cardHeader = { padding: "16px 20px", borderBottom: "1px solid #f0f0f0" };
const cardTitle  = { margin: 0, fontSize: 15, fontWeight: 600, color: "#2c3e50" };
const cardBody   = { padding: 20, transition: "opacity 0.2s" };
const fieldRow   = { display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" };
const fieldBox   = { flex: 1, minWidth: 240, display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel = { fontSize: 12, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" };
const input      = { padding: "8px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, width: "100%", boxSizing: "border-box" };
const btnTest    = { padding: "8px 16px", borderRadius: 8, border: "1px solid #007bff", background: "#007bff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" };
const btnPrimary = { padding: "9px 22px", borderRadius: 8, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const eventGrid  = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const eventCard  = { background: "#f8f9fb", borderRadius: 10, padding: "14px 16px", border: "1px solid #eef0f3" };
const tgBadge    = { fontSize: 10, fontWeight: 700, background: "#e8f4fd", color: "#0088cc", padding: "2px 8px", borderRadius: 10, letterSpacing: "0.3px" };
const helpBox    = { marginTop: 16, background: "#f0f7ff", borderRadius: 10, padding: "16px 18px", border: "1px solid #cce5ff" };
const helpTitle  = { fontWeight: 700, fontSize: 13, color: "#0055a5", marginBottom: 10 };
const helpList   = { margin: 0, paddingLeft: 20, fontSize: 13, color: "#333", lineHeight: 1.8 };
const helpNote   = { marginTop: 12, fontSize: 12, color: "#555", background: "#fff", borderRadius: 8, padding: "10px 12px", border: "1px solid #dee2e6" };
const link       = { color: "#0088cc" };
const code       = { background: "#e9ecef", padding: "1px 5px", borderRadius: 4, fontFamily: "monospace", fontSize: 12 };
